import { CellItem } from '@components/UI-Components/HierarchyWiseItemsTable/components/Row/Cell'
import useOrderContext from '@modules/order/hooks/use-order-context'
import useViewContext from '@modules/order/hooks/use-view-context'
import { useObserver } from 'mobx-react-lite'
import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react'
import {
  checkWhetherBookedOrNot,
  useEnhancedProductStatus
} from '@components/UI-Components/ProductCard/ProductStatusHook'
import filter from 'lodash/filter'
import { makeStyles } from '@material-ui/core'
import IADoorQuantityTopFields from './IADoorQuantityTopFields'
import IAProductSizes from './IAProductSizes'
import { isCaptureSizeEnabled } from '@modules/order/utils/table-column-utils'
import { isEmpty } from 'lodash'
import { MessageDialog } from '@components/UI-Components/MessageDialog'
import { useTranslation } from 'react-i18next'
import { getNumberFromLocale } from '@utils/get-number-form-locale'
import { InitialAllocationUIStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/InitialAllocationUIStore'
import { InitialAllocationDataStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/InitialAllocationDataStore'
import { productCurveStore } from '@stores/productCurvesStore'
import { nav } from '@stores'

const useStyles = makeStyles(theme => ({
  bookQty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: 'max-content'
  }
}))

const IADoorQuantityCell: FC<CellItem> = ({ rowData }) =>
  useObserver(function useHook() {
    const { uiStore } = useViewContext()
    const {
      getBookedDoors,
      hideShowProductSizes,
      showProductSizes,
      getSizes,
      bulkUpdateDeliveryDoorOrderProducts,
      dataStore: { getIAProductDataLoading, getIAProductData }
    } = uiStore as InitialAllocationUIStore<InitialAllocationDataStore>
    const {
      uiStore: {
        isOrderEditable,
        dataStore: { summary }
      }
    } = useOrderContext()
    const {
      queryParams: { clientId, buyingSessionGroupId }
    } = nav
    const productRowId = rowData?.id
    const { t } = useTranslation()
    const [openDialog, setOpenDialog] = useState(false)
    const classes = useStyles()
    const isProductActive: boolean = useEnhancedProductStatus(rowData)
    const [productCurves, setProductCurves] = useState(null)

    const isAPICallInProgress = rowData?.id ? getIAProductDataLoading(rowData.id) : true

    const disabled = useMemo(
      () => !isOrderEditable || !isProductActive || isAPICallInProgress,
      [isAPICallInProgress, isOrderEditable, isProductActive]
    )

    const bookedDoors = getBookedDoors(productRowId, summary?.doorOrders)
    const areAllDoorsBooked = useMemo(() => {
      return checkWhetherBookedOrNot(
        rowData?.excludedInDoors,
        filter(summary?.doorOrders, doorOrder => !doorOrder?.deleted),
        bookedDoors,
        true
      )
    }, [summary?.doorOrders, bookedDoors])

    const noBookedDoors = bookedDoors?.length === 0
    const disableIASplitDoorQty = disabled || noBookedDoors

    const splitToDoorHandler = useCallback(
      currentValue => {
        const productDoorCurves = productCurves?.doorCurve ?? {}
        if (isEmpty(productDoorCurves) || isEmpty(productDoorCurves?.curves)) {
          setOpenDialog(true)
        } else {
          bulkUpdateDeliveryDoorOrderProducts(
            summary,
            rowData.id,
            getNumberFromLocale(currentValue)
          )
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      },
      [productCurves?.doorCurve, bulkUpdateDeliveryDoorOrderProducts, summary]
    )

    useEffect(() => {
      if (productRowId) {
        const curves = productCurveStore.getCurvesForProduct(
          rowData.id,
          clientId,
          buyingSessionGroupId
        )
        setProductCurves(curves)
      }
    }, [productRowId, clientId, buyingSessionGroupId])

    if (!productRowId) return <></>
    const IAProductData = getIAProductData(productRowId) || {}
    const totalSizeQuantity = rowData?.doorOrderProductSizeQuantity || 0
    const IATotalSizeQuantity = IAProductData?.deliverySizeQuantity || 0
    const totalDoorQuantity = rowData?.doorOrderProductDoorQuantity || 0
    const IATotalDoorQuantity = IAProductData?.deliveryAllocatedProductQuantity || 0
    const IAWarehouseQuantity = IAProductData?.deliveryRemainingProductQuantity || 0
    const actualIADoorQuantity = IAProductData?.deliveryDoorQuantity || 0
    const IAProductQuantity = IAProductData?.deliveryProductQuantity || 0

    return (
      <div className={classes.bookQty} data-test-id='IADoorQuantityCell'>
        <IADoorQuantityTopFields
          rowData={rowData}
          areAllDoorsBooked={areAllDoorsBooked}
          totalDoorQuantity={totalDoorQuantity}
          disabled={disabled}
          IATotalDoorQuantity={IATotalDoorQuantity}
          IAWarehouseQuantity={IAWarehouseQuantity}
          actualIADoorQuantity={actualIADoorQuantity}
          IAProductQuantity={IAProductQuantity}
          splitToDoorHandler={splitToDoorHandler}
          disableIASplitDoorQty={disableIASplitDoorQty}
        />
        <IAProductSizes
          rowData={rowData}
          totalSizeQuantity={totalSizeQuantity}
          IATotalSizeQuantity={IATotalSizeQuantity}
          hasCaptureSize={isCaptureSizeEnabled(summary)}
          hideShowProductSizes={hideShowProductSizes}
          showProductSizes={showProductSizes[productRowId]}
          sizesList={getSizes(productRowId)}
        />
        {openDialog ? (
          <MessageDialog
            open={openDialog}
            title={t('common.label.notAllowed', 'Not Allowed')}
            message={t(
              'order.messages.splitToDoorErrorMessage',
              'Sorry, but there is no door distribution information available, please contact your showroom administrator for help.'
            )}
            handleClose={() => setOpenDialog(false)}
            closeButtonTitle={t('bb.common.label.ok', 'Ok')}
          />
        ) : null}
      </div>
    )
  })

export default memo(IADoorQuantityCell)
